import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import Button from '../../components/Button';
import { UI_TEXT, getRandomAnalyzingMessage } from '../../config/ui-text';
import { SurveyAnalyzingIndicator } from './SurveyAnalyzingIndicator';
import { getNextDynamicQuestion, submitDynamicAnswer } from '../../utils/surveyApi';
import { AiDynamicSectionQuestionInput } from './AiDynamicSectionQuestionInput';
import type { VoiceOrTextTranscribeContext } from './VoiceOrTextInput';
import { questionImageSrcFromKey } from './aiDynamicSection';
import { mediaSrc } from '../../components/report/mediaSrc';
import {
  buildAnswerPayload,
  isAnswerComplete,
  STOP_ERROR,
  type AiDynamicSectionQuestion,
  type DynamicSectionEmittedQuestion,
  type DynamicSectionStopReason,
} from './aiDynamicSection';

/** Widens `AiDynamicSectionQuestion` to admit `disable_voice_input`/`disable_text_input`, which
 *  live on the shared `ParsedQuestion` row one level up and are present at runtime via
 *  `SurveySession.tsx`'s cast — without touching `aiDynamicSection.ts` itself. */
export type AiDynamicSectionQuestionWithVoiceFlags = AiDynamicSectionQuestion & {
  disable_voice_input?: boolean;
  disable_text_input?: boolean;
};

export interface AiDynamicSectionRunnerProps {
  question: AiDynamicSectionQuestionWithVoiceFlags;
  sessionId: number;
  onSectionDone: (stopReason: DynamicSectionStopReason) => void;
}

const SLOT_SEPARATOR = '|';
const LONG_WAIT_NOTICE_MS = 60_000;
const { LONG_WAIT_HEADLINE, LONG_WAIT_DETAIL } = UI_TEXT.HUMAN_SURVEY.SESSION.MESSAGES;

/**
 * Drives one `ai_dynamic_section` end to end: fetches a turn, renders its input widget, submits
 * the answer, loops until the server says done. All state lives here, not in `SurveySession.tsx`
 * (spec-01: that file stays at its delegation branch only — see ADR-0002 §3). No `stop_reason` is
 * ever shown to the respondent, including `'error'` — a prober failure fails open. Voice channel
 * state (`showTextInput`/`transcriptionUnavailable`) is owned HERE, scoped to this section only —
 * see ADR-0002 §4.
 */
export function AiDynamicSectionRunner({
  question,
  sessionId,
  onSectionDone,
}: AiDynamicSectionRunnerProps) {
  const [turn, setTurn] = useState<DynamicSectionEmittedQuestion | null>(null);
  const [answer, setAnswer] = useState<unknown>(undefined);
  const [isBusy, setIsBusy] = useState(false);
  // Picked once per wait rather than per render, so the message does not flicker between frames.
  const [analyzingMessage, setAnalyzingMessage] = useState(getRandomAnalyzingMessage);
  const [showTextInput, setShowTextInput] = useState(false);
  const [transcriptionUnavailable, setTranscriptionUnavailable] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [waitIsLong, setWaitIsLong] = useState(false);

  // Synchronous re-entrancy latch (task-9): state alone isn't enough — two clicks in the same tick
  // both read the old `isBusy=false`. A ref flips immediately, before the next render.
  const inFlightRef = useRef(false);

  // Forces the input channel when the author disabled one, or transcription has broken (mirrors
  // SurveySession's own forcing effect). Re-evaluated every turn so it stays enforced all probe long.
  const forcedChannelRef = useRef(false);
  // The turn the SURVEY opened the text box on (a recording that did not work). It ends with that
  // turn; the respondent's own switch to typing carries over. Mirrors SurveySession.
  const surveyForcedTextForRef = useRef<string | null>(null);
  const turnId = turn?.question_id ?? null;
  const handleShowTextInputChange = useCallback(
    (show: boolean, forcedBySurvey = false) => {
      surveyForcedTextForRef.current = show && forcedBySurvey ? turnId : null;
      setShowTextInput(show);
    },
    [turnId],
  );
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect -- deriving the forced channel off props IS
       the synchronization this effect exists for. Mirrors SurveySession.tsx's own equivalent. */
    const forcedOn = surveyForcedTextForRef.current;
    const surveyForcedCarriedOver = forcedOn !== null && forcedOn !== turnId;
    if (surveyForcedCarriedOver) surveyForcedTextForRef.current = null;
    if (transcriptionUnavailable) {
      setShowTextInput(true);
      return;
    }
    if (question.disable_voice_input) {
      setShowTextInput(true);
      forcedChannelRef.current = true;
    } else if (question.disable_text_input) {
      setShowTextInput(false);
      forcedChannelRef.current = true;
    } else if (forcedChannelRef.current || surveyForcedCarriedOver) {
      setShowTextInput(false);
      forcedChannelRef.current = false;
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [turnId, question.disable_voice_input, question.disable_text_input, transcriptionUnavailable]);

  const fetchNext = useCallback(async () => {
    setAnalyzingMessage(getRandomAnalyzingMessage());
    setIsBusy(true);
    try {
      const result = await getNextDynamicQuestion(sessionId, question.id);
      if ('done' in result) {
        onSectionDone(result.stop_reason);
        return;
      }
      setTurn(result.question);
      setAnswer(undefined);
      setVoiceError(null);
    } catch {
      // Fail open, matching the backend's own posture: a broken network call must never
      // strand the respondent on this question.
      onSectionDone(STOP_ERROR);
    } finally {
      setIsBusy(false);
    }
  }, [sessionId, question.id, onSectionDone]);

  useEffect(() => {
    // Runs once per section entry (keyed on the section question id) - fetchNext itself
    // re-fetches on every subsequent turn via handleSubmit, not via this effect re-firing.
    // Guarded by inFlightRef like every other trigger of fetchNext (task-9): a second mount of
    // this effect for the same section (StrictMode's double-invoke, or a rapid remount) must not
    // dispatch a second `/next` and spend a second LLM-generated question.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    fetchNext().finally(() => {
      inFlightRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id]);

  /**
   * `submitted` is passed explicitly by the spoken path rather than read from `answer`: the
   * transcript's `onChange` and its submit happen in the same tick, so state still holds the empty
   * string the respondent started with, and the prober would be handed nothing.
   */
  const handleSubmit = async (submitted: unknown = answer) => {
    if (!turn || inFlightRef.current) return;
    inFlightRef.current = true;
    setAnalyzingMessage(getRandomAnalyzingMessage());
    setIsBusy(true);
    try {
      const payload = buildAnswerPayload(turn, submitted);
      const result = await submitDynamicAnswer(sessionId, question.id, payload);
      if ('done' in result) {
        onSectionDone(result.stop_reason);
        return;
      }
      await fetchNext();
    } catch {
      onSectionDone(STOP_ERROR);
    } finally {
      setIsBusy(false);
      inFlightRef.current = false;
    }
  };

  const isWaiting = !turn || isBusy;
  // One wait runs from the click to the next question, across both requests, so the clock starts
  // and clears on isWaiting alone. Past a minute, the respondent is told it is not stuck.
  useEffect(() => {
    if (!isWaiting) return;
    const timer = setTimeout(() => setWaitIsLong(true), LONG_WAIT_NOTICE_MS);
    return () => {
      clearTimeout(timer);
      setWaitIsLong(false);
    };
  }, [isWaiting]);

  // While a turn is being written — on first load AND between turns — show the SAME screen the
  // rest of the survey shows while the AI works. Previously the between-turns wait had no screen
  // at all, only a disabled button, and a respondent reported it as a freeze.
  if (isWaiting) {
    return (
      <SurveyAnalyzingIndicator
        message={waitIsLong ? LONG_WAIT_HEADLINE : analyzingMessage}
        detail={waitIsLong ? LONG_WAIT_DETAIL : 'Preparing your next question…'}
      />
    );
  }

  /* The turn names an asset, the pool holds its key — ADR-0008. Null wherever it cannot be
     resolved: a broken frame is worse than no image, since the question is asked either way. */
  const shownImageSrc = (() => {
    const assetId = turn.media_asset_id;
    if (!assetId) return null;
    const asset = (question.media_pool ?? []).find((a) => a.asset_id === assetId);
    if (!asset?.s3_key) return null;
    const path = questionImageSrcFromKey(asset.s3_key);
    // `mediaSrc` puts the API origin in front: the path is relative, and the frontend is served
    // from its own origin, so a bare one fetches index.html instead of a picture.
    return path ? mediaSrc(path) : null;
  })();

  const transcribeContext: VoiceOrTextTranscribeContext = {
    sessionId,
    questionId: turn.question_id,
  };

  return (
    <div className="space-y-6">
      {shownImageSrc && (
        /* Alt text says what the image is FOR, never what is in it — ADR-0008. */
        <img
          src={shownImageSrc}
          alt="Shown with this question"
          className="max-h-80 w-auto rounded-lg border border-gray-200"
        />
      )}
      <p className="text-lg font-medium text-gray-900">{turn.text}</p>
      <AiDynamicSectionQuestionInput
        question={turn}
        answer={answer}
        onAnswerChange={setAnswer}
        voice={{
          showTextInput,
          onShowTextInputChange: handleShowTextInputChange,
          transcriptionUnavailable,
          onTranscriptionUnavailable: () => setTranscriptionUnavailable(true),
          disableVoiceInput: !!question.disable_voice_input,
          disableTextInput: !!question.disable_text_input,
          slotKey: `${question.id}${SLOT_SEPARATOR}${turn.question_id}`,
          transcribeContext,
          onError: setVoiceError,
          // Stopping the mic is the submit, exactly as it is for an ordinary spoken question —
          // only later, because this answer is CONSUMED by the next turn rather than patched in.
          onSpokenAnswerTranscribed: (spoken) => {
            if (!isAnswerComplete(turn, spoken)) return;
            setAnswer(spoken);
            void handleSubmit(spoken);
          },
        }}
      />
      {voiceError && (
        <p className="text-sm text-red-600" role="alert">
          {voiceError}
        </p>
      )}
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={() => handleSubmit()}
          disabled={isBusy || !isAnswerComplete(turn, answer)}
          icon={<ArrowRight className="w-4 h-4" />}
        >
          {UI_TEXT.HUMAN_SURVEY.SESSION.BUTTONS.NEXT}
        </Button>
      </div>
    </div>
  );
}
